import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Wallet, CheckCircle2, AlertCircle, Loader2, Play, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const fmtEur = (n) => (n === null || n === undefined ? "—" : `€${Number(n).toFixed(2)}`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");

export default function AdminPayments() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [copiedIban, setCopiedIban] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/payments");
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error("Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runCompute = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/payments/run-compute");
      toast.success(`Fee ricalcolate (${r.data.processed} PA, mese ${r.data.month})`);
      await load();
    } catch { toast.error("Errore"); } finally { setRunning(false); }
  };

  const runPayouts = async () => {
    if (!window.confirm("Eseguire ora i trasferimenti Stripe ai PA configurati?")) return;
    setRunning(true);
    try {
      const r = await api.post("/admin/payments/run-payouts");
      toast.success(`Trasferimenti: ${r.data.success} ok, ${r.data.fail} ko, ${r.data.manual} da pagare a mano`);
      await load();
    } catch { toast.error("Errore"); } finally { setRunning(false); }
  };

  const markPaid = async (id) => {
    if (!window.confirm("Confermi di aver pagato manualmente questo PA?")) return;
    try {
      await api.post(`/admin/users/${id}/mark-fee-paid`);
      toast.success("Pagamento registrato");
      await load();
    } catch { toast.error("Errore"); }
  };

  const copyIban = (iban, id) => {
    navigator.clipboard.writeText(iban);
    setCopiedIban(id);
    toast.success("IBAN copiato");
    setTimeout(() => setCopiedIban(null), 2000);
  };

  if (loading) return <div className="text-gray-400">Caricamento…</div>;

  return (
    <div className="space-y-6 fade-slide">
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-[#0A0A0A] flex items-center gap-2">
            <Wallet size={26} className="text-[#B40101]" /> Pagamenti
          </h1>
          <p className="text-gray-500 mt-1">Gestione fee Property Advisor: automatici via Stripe Connect, manuali via bonifico.</p>
        </div>
        <div className="flex gap-2">
          <button className="vg-btn-secondary inline-flex items-center gap-2 px-4 py-2 text-sm" onClick={runCompute} disabled={running} data-testid="payments-run-compute">
            {running ? <Loader2 className="animate-spin" size={14} /> : <Play size={14} />} Ricalcola fee
          </button>
          <button className="vg-btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm" onClick={runPayouts} disabled={running} data-testid="payments-run-payouts">
            {running ? <Loader2 className="animate-spin" size={14} /> : <Play size={14} />} Esegui trasferimenti
          </button>
        </div>
      </div>

      <div className="vg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3 text-left">PA</th>
              <th className="px-4 py-3 text-left">P.IVA</th>
              <th className="px-4 py-3 text-center">Fee mese</th>
              <th className="px-4 py-3 text-center">Da pagare</th>
              <th className="px-4 py-3 text-center">Stripe Connect</th>
              <th className="px-4 py-3 text-center">Ultimo pagamento</th>
              <th className="px-4 py-3 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <tr className="border-t border-gray-100">
                  <td className="px-4 py-3 font-semibold text-[#0A0A0A]" data-testid={`pay-row-${r.id}`}>
                    {r.nome}<br /><span className="text-xs text-gray-400 font-normal">{r.email}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{r.partita_iva || "—"}</td>
                  <td className="px-4 py-3 text-center">{fmtEur(r.fee_mese_corrente)}</td>
                  <td className="px-4 py-3 text-center"><span className={`font-bold ${r.fee_da_pagare > 0 ? "text-[#B40101]" : "text-gray-400"}`}>{fmtEur(r.fee_da_pagare)}</span></td>
                  <td className="px-4 py-3 text-center">
                    {r.stripe_payouts_enabled ? (
                      <span className="inline-flex items-center gap-1 text-green-600 text-xs font-bold"><CheckCircle2 size={14} /> Attivo</span>
                    ) : r.stripe_account_id ? (
                      <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-bold"><AlertCircle size={14} /> Incompleto</span>
                    ) : (
                      <span className="text-gray-400 text-xs">Pagamento manuale</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-xs text-gray-500">{fmtDate(r.last_fee_payment_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button className="text-xs text-[#B40101] font-semibold hover:underline mr-3" onClick={() => setExpanded(expanded === r.id ? null : r.id)} data-testid={`pay-toggle-${r.id}`}>
                      {expanded === r.id ? "Chiudi" : "Storico"}
                    </button>
                    {!r.stripe_payouts_enabled && r.fee_da_pagare > 0 && (
                      <button className="vg-btn-secondary px-3 py-1 text-xs" onClick={() => markPaid(r.id)} data-testid={`pay-mark-${r.id}`}>
                        Segna pagato
                      </button>
                    )}
                  </td>
                </tr>
                {expanded === r.id && (
                  <tr className="bg-gray-50">
                    <td colSpan={7} className="px-4 py-4">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-bold uppercase text-gray-500">IBAN</span>
                          <span className="font-mono text-xs text-[#0A0A0A]">{r.iban || "—"}</span>
                          {r.iban && (
                            <button className="vg-btn-secondary px-2 py-1 text-xs inline-flex items-center gap-1" onClick={() => copyIban(r.iban, r.id)}>
                              {copiedIban === r.id ? <Check size={12} /> : <Copy size={12} />} Copia
                            </button>
                          )}
                        </div>
                        <div>
                          <span className="text-xs font-bold uppercase text-gray-500">Ultimi 6 trasferimenti</span>
                          {r.history.length === 0 ? (
                            <p className="text-xs text-gray-400 mt-1">Nessun trasferimento ancora.</p>
                          ) : (
                            <table className="w-full text-xs mt-2">
                              <tbody>
                                {r.history.map((h, i) => (
                                  <tr key={i}>
                                    <td className="py-1">{fmtDate(h.date)}</td>
                                    <td className="py-1">{h.month_label}</td>
                                    <td className="py-1 font-mono">{fmtEur(h.amount_eur)}</td>
                                    <td className="py-1"><span className={h.status === "success" ? "text-green-600" : "text-[#B40101]"}>{h.status}</span></td>
                                    <td className="py-1 text-gray-400 truncate max-w-xs">{h.stripe_transfer_id || h.error_message || "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-400">Nessun Property Advisor da gestire.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
